import React, { useState } from 'react';
import { LOCATIONS_WITH_COORDS, DEBUG_MODE } from './constants';
import { getLocationName } from './locationUtils';
import { getProgramType, getProgramColor, formatTimeRange } from './helpers';

const Step4FindNextAvailable = React.memo(function Step4FindNextAvailable({
  patientLocationInfo,
  nearestLocations,
  userType,
  assignedLocation,
  isLoadingNextAvailable,
  selectedSearchLocation,
  handleFindNextAvailable,
  showNextAvailable,
  nextAvailableResults,
  searchComplete,
  selectedNextAvailableSlot,
  handleSelectNextAvailableSlot,
  selectedTimeSlot,
  selectedProvider,
  selectedLocation,
  selectedVisitDate,
  bookingData,
  setShowNextAvailable,
  setNextAvailableResults,
  handleNextStep,
}) {
  const [timeFilter, setTimeFilter] = useState('all'); // 'all' | 'morning' | 'afternoon'

  // Apply client-side time-of-day filter to slots within each provider card
  const filterSlots = (slots) => {
    if (timeFilter === 'all') return slots;
    return slots.filter(slot => {
      const [t, period] = slot.time.split(' ');
      const hour = parseInt(t.split(':')[0]);
      if (timeFilter === 'morning') {
        return period === 'AM' || (period === 'PM' && hour === 12);
      }
      return period === 'PM' && hour !== 12;
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Provider &amp; Schedule</h2>
        <p className="text-gray-600">Choose your preferred provider and appointment time</p>
      </div>

      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 shadow-lg">
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500 rounded-full mb-3">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-orange-900 mb-2">Find Next Available Appointment</h3>
          <p className="text-orange-700 text-sm mb-4">
            Select a location to find the soonest available appointments
          </p>

          {patientLocationInfo && (
            <div className="inline-flex items-center bg-white border border-orange-300 rounded-lg px-4 py-2 mb-3">
              <svg className="w-4 h-4 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm text-gray-800">
                <strong>Your Location:</strong> {patientLocationInfo.city}, {patientLocationInfo.state} {patientLocationInfo.zipcode}
              </span>
            </div>
          )}

          {nearestLocations.length > 0 ? (
            <>
              {nearestLocations.length === 1 && nearestLocations[0].isVirtual && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 mb-4 max-w-2xl mx-auto">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">No Physical Locations Nearby</h4>
                      <p className="text-sm text-blue-800">
                        You are more than 30 miles from any physical clinic location. We recommend scheduling a virtual appointment through <strong>Homepsych</strong> for convenient online care.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className={`grid gap-3 max-w-2xl mx-auto ${
                nearestLocations.length === 1
                  ? 'grid-cols-1 justify-items-center'
                  : nearestLocations.length === 2
                    ? 'grid-cols-2 justify-items-center max-w-lg'
                    : 'grid-cols-2 md:grid-cols-3'
              }`}>
                {(() => {
                  let locationsToDisplay = nearestLocations;
                  if (userType === 'existing' && assignedLocation) {
                    locationsToDisplay = nearestLocations.filter(loc => loc.id === assignedLocation.id);
                    if (locationsToDisplay.length === 0) {
                      const assignedLoc = LOCATIONS_WITH_COORDS[assignedLocation.id];
                      if (assignedLoc) locationsToDisplay = [assignedLoc];
                    }
                  }
                  return locationsToDisplay.map(location => {
                    const isSearching = isLoadingNextAvailable && selectedSearchLocation?.id === location.id;
                    const showDistance = location.distance !== undefined && location.distance > 0;
                    const isClosest = location.isClosest;
                    const isSelected = selectedSearchLocation?.id === location.id;

                    return (
                      <button
                        key={location.id}
                        onClick={() => handleFindNextAvailable(location)}
                        disabled={isLoadingNextAvailable}
                        className={`rounded-xl border-2 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                          isSelected
                            ? 'bg-green-50 border-green-500 ring-2 ring-green-300'
                            : 'bg-white border-orange-300 hover:border-orange-500 hover:bg-orange-50'
                        } ${nearestLocations.length <= 2 ? 'p-6 min-w-[200px]' : 'p-4'}`}
                      >
                        <div className="text-center">
                          {isClosest && (
                            <div className="mb-1">
                              <svg className="w-5 h-5 text-blue-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            </div>
                          )}
                          <svg
                            className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-green-600' : 'text-orange-600'}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            {location.isVirtual ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            )}
                          </svg>
                          <div className="font-semibold text-sm mb-1 text-gray-900">
                            {location.name.replace(' - NAMHS', '')}
                          </div>
                          {isClosest && (
                            <div className="text-xs text-blue-700 font-bold mb-1 flex items-center justify-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              Closest to You
                            </div>
                          )}
                          {isSelected && (
                            <div className="text-xs text-green-700 font-bold mb-1 flex items-center justify-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Searching...
                            </div>
                          )}
                          {showDistance && (
                            <div className="text-xs text-gray-600 mb-1">
                              {location.distance.toFixed(1)} mi away
                            </div>
                          )}
                          {location.isVirtual && (
                            <div className="text-xs text-blue-600 font-medium">Virtual/Online</div>
                          )}
                          {isSearching && (
                            <div className="mt-2">
                              <svg className="animate-spin h-4 w-4 text-orange-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-blue-800 text-sm">
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Location filtering will be available after entering your patient information and zipcode above.
              </p>
            </div>
          )}
        </div>

        {showNextAvailable && isLoadingNextAvailable && (
          <div className="mt-6 p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
            <div className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 text-orange-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-orange-800 font-medium">
                {nextAvailableResults.length > 0
                  ? `Searching... Found ${nextAvailableResults.length} day(s) with availability so far.`
                  : 'Searching for available appointments...'}
              </span>
            </div>
            {nextAvailableResults.length > 0 && (
              <p className="text-orange-600 text-sm mt-2 text-center">
                Results are updating in real-time as we find them...
              </p>
            )}
          </div>
        )}

        {showNextAvailable && searchComplete && !isLoadingNextAvailable && (
          <div className="mt-4 p-3 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-green-800 font-medium">
                Search Complete! {nextAvailableResults.length > 0
                  ? `Found ${nextAvailableResults.length} day(s) with available appointments.`
                  : 'No available appointments found in the next 10 business days.'}
              </span>
            </div>
          </div>
        )}

        {showNextAvailable && nextAvailableResults.length === 0 && !isLoadingNextAvailable && searchComplete && (
          <div className="mt-6 p-6 bg-orange-50 border-2 border-orange-300 rounded-lg animate-fadeIn">
            <div className="text-center">
              <svg className="w-12 h-12 text-orange-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 className="text-lg font-semibold text-orange-900 mb-2">No Appointments Found</h4>
              <p className="text-orange-700 text-sm mb-4">
                {selectedSearchLocation
                  ? `We couldn't find any available appointments at ${selectedSearchLocation.name.replace(' - NAMHS', '')} in the next 10 business days (Mon-Fri).`
                  : "We couldn't find any available appointments in the next 10 business days (Mon-Fri)."}
              </p>
              <p className="text-orange-600 text-sm">
                Please try a different location, use manual selection below, or contact the office directly for assistance.
              </p>
            </div>
          </div>
        )}

        {showNextAvailable && nextAvailableResults.length > 0 && (
          <div className="mt-6 space-y-4 animate-fadeIn">
            <div className="border-t-2 border-orange-200 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h4 className="text-lg font-semibold text-orange-900 text-center sm:text-left">
                  📅 {selectedSearchLocation
                    ? `Available Appointments at ${selectedSearchLocation.name.replace(' - NAMHS', '')}`
                    : 'Available Appointments (Next 10 Business Days)'}
                </h4>
                {/* Time-of-day filter chips */}
                <div className="flex items-center justify-center sm:justify-end gap-2">
                  <span className="text-xs text-gray-500 mr-1">Filter:</span>
                  {[
                    { value: 'all', label: 'All Times' },
                    { value: 'morning', label: '☀️ Morning' },
                    { value: 'afternoon', label: '🌤 Afternoon' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setTimeFilter(opt.value)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        timeFilter === opt.value
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-orange-400 hover:text-orange-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {nextAvailableResults.map((dateGroup, dateIdx) => {
                if (DEBUG_MODE && dateIdx === 0) {
                  console.log('🎨 [RENDERING] Displaying results to user:', {
                    totalDateGroups: nextAvailableResults.length,
                    firstDateGroup: dateGroup
                  });
                }
                return (
                  <div key={dateIdx} className="mb-6">
                    <div className="bg-orange-600 text-white px-4 py-3 rounded-t-lg font-semibold">
                      {dateGroup.dateDisplay}
                    </div>
                    <div className="bg-white border-2 border-orange-200 rounded-b-lg p-4 space-y-4">
                      {dateGroup.locations.map((location, locIdx) => (
                        <div key={locIdx} className="border-l-4 border-orange-400 pl-4">
                          <h5 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {location.name}
                          </h5>
                          <div className="space-y-3">
                            {location.providers.map((provider, provIdx) => {
                              const filteredSlots = filterSlots(provider.timeSlots);
                              const morningSlots = filteredSlots.filter(slot => {
                                const [time, period] = slot.time.split(' ');
                                const [hours] = time.split(':').map(Number);
                                return period === 'AM' || (period === 'PM' && hours === 12);
                              });
                              const afternoonSlots = filteredSlots.filter(slot => {
                                const [time, period] = slot.time.split(' ');
                                const [hours] = time.split(':').map(Number);
                                return period === 'PM' && hours !== 12;
                              });
                              // Hide the entire provider card if filter removes all their slots
                              if (timeFilter !== 'all' && filteredSlots.length === 0) return null;
                              return (
                                <div key={provIdx} className="bg-orange-50 rounded-lg p-3">
                                  <div className="mb-2">
                                    <div className="font-medium text-gray-900 flex items-center">
                                      <svg className="w-4 h-4 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                      {provider.name}
                                    </div>
                                    <div className="ml-6 mt-1">
                                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getProgramColor(getProgramType(provider.name))}`}>
                                        {getProgramType(provider.name)}
                                      </span>
                                    </div>
                                  </div>

                                  {morningSlots.length > 0 && (
                                    <div className="mb-3">
                                      <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center">
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        Morning
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {morningSlots.map((slot, timeIdx) => {
                                          const timeRange = formatTimeRange(slot.time, slot.duration);
                                          const isSelected = selectedNextAvailableSlot &&
                                            selectedNextAvailableSlot.date === dateGroup.date &&
                                            selectedNextAvailableSlot.facilityId === location.facilityId &&
                                            selectedNextAvailableSlot.providerId === provider.id &&
                                            selectedNextAvailableSlot.timeSlot === slot.time;
                                          return (
                                            <button
                                              key={timeIdx}
                                              onClick={() => handleSelectNextAvailableSlot(dateGroup.date, dateGroup.dateDisplay, location.facilityId, provider, slot.time, slot.scheduleId, slot.duration)}
                                              className={`px-3 py-2 border-2 rounded-lg transition-all font-medium text-xs shadow-sm hover:shadow ${
                                                isSelected
                                                  ? 'bg-green-500 border-green-600 text-white ring-2 ring-green-300 ring-offset-2'
                                                  : 'bg-white border-orange-300 text-orange-700 hover:bg-orange-100 hover:border-orange-500'
                                              }`}
                                            >
                                              <div className="text-center">
                                                <div className="font-semibold">{timeRange.displayRange}</div>
                                                <div className="text-[10px] opacity-75">({slot.duration} min)</div>
                                                {isSelected && <div className="text-[10px] font-bold mt-1">✓ Selected</div>}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {afternoonSlots.length > 0 && (
                                    <div>
                                      <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center">
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        </svg>
                                        Afternoon
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {afternoonSlots.map((slot, timeIdx) => {
                                          const timeRange = formatTimeRange(slot.time, slot.duration);
                                          const isSelected = selectedNextAvailableSlot &&
                                            selectedNextAvailableSlot.date === dateGroup.date &&
                                            selectedNextAvailableSlot.facilityId === location.facilityId &&
                                            selectedNextAvailableSlot.providerId === provider.id &&
                                            selectedNextAvailableSlot.timeSlot === slot.time;
                                          return (
                                            <button
                                              key={timeIdx}
                                              onClick={() => handleSelectNextAvailableSlot(dateGroup.date, dateGroup.dateDisplay, location.facilityId, provider, slot.time, slot.scheduleId, slot.duration)}
                                              className={`px-3 py-2 border-2 rounded-lg transition-all font-medium text-xs shadow-sm hover:shadow ${
                                                isSelected
                                                  ? 'bg-green-500 border-green-600 text-white ring-2 ring-green-300 ring-offset-2'
                                                  : 'bg-white border-orange-300 text-orange-700 hover:bg-orange-100 hover:border-orange-500'
                                              }`}
                                            >
                                              <div className="text-center">
                                                <div className="font-semibold">{timeRange.displayRange}</div>
                                                <div className="text-[10px] opacity-75">({slot.duration} min)</div>
                                                {isSelected && <div className="text-[10px] font-bold mt-1">✓ Selected</div>}
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedTimeSlot && selectedProvider && selectedLocation && selectedVisitDate && (
        <div className="appointment-confirmation-box bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl p-5 shadow-lg animate-fadeIn">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4 flex-1">
              <h4 className="text-xl font-bold text-green-900 mb-2">✓ Appointment Time Selected!</h4>
              <div className="space-y-1 text-sm">
                <p className="text-green-800"><strong>Location:</strong> {getLocationName(selectedLocation)}</p>
                {selectedNextAvailableSlot?.providerName && (
                  <p className="text-green-800"><strong>Provider:</strong> {selectedNextAvailableSlot.providerName}</p>
                )}
                <p className="text-green-800">
                  <strong>Date:</strong> {selectedNextAvailableSlot?.dateDisplay || (() => {
                    const [year, month, day] = selectedVisitDate.split('-');
                    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    return dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                  })()}
                </p>
                <p className="text-green-800">
                  <strong>Time:</strong> {(() => {
                    const duration = selectedNextAvailableSlot?.duration || bookingData.duration;
                    if (duration) return formatTimeRange(selectedTimeSlot, duration).displayRange;
                    return selectedTimeSlot;
                  })()}
                </p>
              </div>
              <p className="text-green-600 text-xs mt-3 italic">
                {selectedNextAvailableSlot
                  ? 'Your selected time slot is highlighted above. You can select a different time if needed.'
                  : 'Click "Review Appointment" below to continue'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={() => {
            if (showNextAvailable) {
              setShowNextAvailable(false);
              setNextAvailableResults([]);
            }
            handleNextStep();
          }}
          disabled={!(selectedLocation && selectedProvider && selectedVisitDate && selectedTimeSlot)}
          className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Review Appointment
        </button>
      </div>
    </div>
  );
});

export default Step4FindNextAvailable;
